import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Download, FileCode2, Image as ImageIcon, Printer } from "lucide-react";
import { loadBwip } from "@/lib/bwip";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { breadcrumbJsonLd, ldScript, pageHead, softwareAppJsonLd } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/itf-14-label-generator";
const TITLE = "ITF-14 Label Generator — Carton & Shipping Barcodes";
const DESC =
  "Free ITF-14 carton label generator. Render the GTIN-14 with bearer bars plus an optional GS1-128 batch/expiry barcode. PNG or SVG output.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "ITF-14 Label Generator", path: PATH },
];

const FAQ = [
  {
    q: "What is an ITF-14 carton label?",
    a: "ITF-14 (Interleaved 2-of-5, 14 digits) is the standard GS1 barcode for shipping cartons and outer cases. The label has the barcode framed by heavy bearer bars to protect against partial scans and prints at a known nominal size on corrugated board.",
  },
  {
    q: "What Goes in the Secondary GS1-128 Barcode?",
    a: "If you fill in Batch / Lot or Expiry, this tool adds a GS1-128 below the ITF-14 carrying (10)<batch>(17)<YYMMDD>. That follows GS1's recommendation to keep the ITF-14 limited to the GTIN-14 and put attributes (batch, expiry, serial) in a separate GS1-128 on the same label.",
  },
  {
    q: "Why Do ITF-14 Carton Barcodes Have a Thick Border?",
    a: "Those are the bearer bars. They equalise the printing pressure across the symbol on flexo-printed corrugated and prevent partial scans where a scanner might decode only some of the bars and accept a truncated value. The bars are part of the GS1 ITF-14 specification, not decoration.",
  },
  {
    q: "Do I Need to Provide the Check Digit?",
    a: "No. Paste the first 13 digits of your GTIN-14 and the generator computes the GS1 mod-10 check digit automatically. If you enter all 14 digits, the check digit is validated against the data and used as-is.",
  },
  {
    q: "Is My Data Uploaded?",
    a: "No. The ITF-14 and GS1-128 are rendered locally with the open-source bwip-js library. Nothing about your GTIN, batch number or expiry leaves your browser.",
  },
  {
    q: "What Size Should I Print the Label At?",
    a: "GS1 nominal width for ITF-14 is 152.4 mm (6 inches), with a magnification factor between about 50% (76 mm) and 100% (152.4 mm). The mm hint under the preview shows the on-screen size at 96 DPI — use 'Actual size' in your print dialog or scale up via your label printer driver.",
  },
  {
    q: "What Is a GTIN-14 Barcode?",
    a: "A GTIN-14 barcode is any GS1 barcode that carries a 14-digit Global Trade Item Number, the identifier for a case or other grouping of a trade item. On corrugated cartons that barcode is usually ITF-14; GS1-128 with AI (01) and GS1 DataMatrix can carry the same number. Retail units normally carry a GTIN-12 or GTIN-13 in a UPC or EAN barcode instead.",
  },
  {
    q: "How Do You Generate an ITF-14 Barcode?",
    a: "Type the 13 digits that precede the check digit into the GTIN-14 field, and this free ITF-14 barcode generator adds the check digit and draws the barcode with its bearer bars. Add a batch number or expiry date if the carton needs them, adjust the scale, and download the label as SVG or PNG or send it to the print dialog. Print at actual size and scan one label before you print the run.",
  },
  {
    q: "What Does an ITF-14 Barcode Look Like?",
    a: "An ITF-14 barcode is a long, wide row of thick and thin black bars with the 14 digits printed underneath. It is usually enclosed in a heavy black rectangle, the bearer bar, or has heavy bars along its top and bottom edges. That frame is the quickest way to tell it apart from the EAN-13 and UPC codes on consumer packs.",
  },
];

function computeMod10Check(digits: string): string {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const d = digits.charCodeAt(digits.length - 1 - i) - 48;
    sum += i % 2 === 0 ? d * 3 : d;
  }
  const mod = sum % 10;
  return String(mod === 0 ? 0 : 10 - mod);
}

function validateGtin(value: string): string | null {
  if (!value) return "Enter the 13 or 14 digit GTIN-14.";
  if (!/^\d+$/.test(value)) return "GTIN-14 only allows digits 0-9.";
  if (value.length !== 13 && value.length !== 14)
    return `GTIN-14 requires 13 or 14 digits (you entered ${value.length}).`;
  if (value.length === 14 && computeMod10Check(value.slice(0, 13)) !== value[13])
    return "Check digit is invalid for this GTIN-14.";
  return null;
}

function withCheckDigit(value: string): string {
  return value.length === 13 ? value + computeMod10Check(value) : value;
}

// GS1 General Specs §7.8.4: AI 10 (batch/lot) is variable-length, up to 20
// characters from the AI 82 / GS1 set (printable ASCII minus a few reserved).
function validateBatch(value: string): string | null {
  if (!value) return null;
  if (value.length > 20) return `Batch is limited to 20 characters (you entered ${value.length}).`;
  if (!/^[!-"%-?A-Z_a-z]+$/.test(value))
    return "Batch must use the GS1 character set: A-Z, a-z, 0-9, and basic punctuation.";
  return null;
}

function validateExpiry(value: string): string | null {
  if (!value) return null;
  if (!/^\d{6}$/.test(value)) return "Expiry must be 6 digits in YYMMDD format.";
  const yy = Number(value.slice(0, 2));
  const mm = Number(value.slice(2, 4));
  const dd = Number(value.slice(4, 6));
  if (mm < 1 || mm > 12) return "Expiry month must be 01-12.";
  // GS1 allows DD=00 to mean "end of month". Accept it.
  if (dd > 31) return "Expiry day must be 00-31.";
  // Reconstruct full year per GS1 §7.12: +50 / -49 window.
  const now = new Date();
  const currentYY = now.getUTCFullYear() % 100;
  const diff = (yy - currentYY + 100) % 100;
  const fullYear = now.getUTCFullYear() + (diff > 50 ? diff - 100 : diff);
  const day = dd === 0 ? new Date(Date.UTC(fullYear, mm, 0)).getUTCDate() : dd;
  const candidate = new Date(Date.UTC(fullYear, mm - 1, day));
  if (
    candidate.getUTCFullYear() !== fullYear ||
    candidate.getUTCMonth() !== mm - 1 ||
    candidate.getUTCDate() !== day
  )
    return "Expiry is not a valid calendar date.";
  return null;
}

function formatExpiryHuman(yyMmDd: string): string {
  if (!/^\d{6}$/.test(yyMmDd)) return yyMmDd;
  const yy = yyMmDd.slice(0, 2);
  const mm = yyMmDd.slice(2, 4);
  const dd = yyMmDd.slice(4, 6);
  const now = new Date();
  const currentYY = now.getUTCFullYear() % 100;
  const diff = (Number(yy) - currentYY + 100) % 100;
  const fullYear = now.getUTCFullYear() + (diff > 50 ? diff - 100 : diff);
  return `${fullYear}-${mm}-${dd === "00" ? "end of month" : dd}`;
}

function safeFilename(gtin: string): string {
  const slug = gtin.replace(/\D+/g, "").slice(0, 16) || "gtin";
  return `itf14-${slug}`;
}

type ItfRender = { svg: string; canvasW: number; canvasH: number } | null;
type Bwip = Awaited<ReturnType<typeof loadBwip>>;

function renderItf14(
  bwipjs: Bwip,
  canvas: HTMLCanvasElement | null,
  gtin14: string,
  scale: number,
  bearerBars: boolean,
  showText: boolean,
): ItfRender {
  // bwip-js's `itf14` bcid emits the standard GS1 bearer-bar frame when
  // `showborder` is true (its default). Turning the toggle off renders the
  // bare ITF symbol without the surrounding border — useful only for
  // pre-printed label stock that already has its own frame.
  const opts: Record<string, unknown> & { bcid: string; text: string } = {
    bcid: "itf14",
    text: gtin14,
    scale,
    height: 20,
    includetext: showText,
    textxalign: "center",
    backgroundcolor: "FFFFFF",
    showborder: bearerBars,
  };
  if (canvas) bwipjs.toCanvas(canvas, opts);
  const svg = bwipjs.toSVG(opts);
  return {
    svg,
    canvasW: canvas?.width ?? 0,
    canvasH: canvas?.height ?? 0,
  };
}

function renderGs1128(
  bwipjs: Bwip,
  canvas: HTMLCanvasElement | null,
  payload: string,
  scale: number,
  showText: boolean,
): ItfRender {
  const opts: Record<string, unknown> & { bcid: string; text: string } = {
    bcid: "gs1-128",
    text: payload,
    parsefnc: true,
    scale,
    height: 14,
    includetext: showText,
    textxalign: "center",
    backgroundcolor: "FFFFFF",
  };
  if (canvas) bwipjs.toCanvas(canvas, opts);
  const svg = bwipjs.toSVG(opts);
  return {
    svg,
    canvasW: canvas?.width ?? 0,
    canvasH: canvas?.height ?? 0,
  };
}

/**
 * Extract the size of a bwip-js SVG so it can be embedded inside a wrapper.
 * bwip-js writes the dimensions as `viewBox="0 0 W H"` (no width/height
 * attributes) so we read from the viewBox.
 */
function readSvgSize(svg: string): { w: number; h: number; inner: string } {
  const vb = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
  const w = vb ? Number(vb[1]) : 0;
  const h = vb ? Number(vb[2]) : 0;
  return { w, h, inner: svg.replace(/^<\?xml[^?]*\?>\s*/, "") };
}

function buildCompositeSvg(
  itf: { svg: string },
  secondary: { svg: string } | null,
  metaLine: string | null,
): string {
  const a = readSvgSize(itf.svg);
  const b = secondary ? readSvgSize(secondary.svg) : null;
  const pad = 12;
  const gap = b ? 14 : 0;
  const metaHeight = metaLine ? 18 : 0;
  const width = Math.max(a.w, b?.w ?? 0) + pad * 2;
  const height = a.h + (b ? gap + b.h : 0) + metaHeight + pad * 2;
  const aX = pad + (width - 2 * pad - a.w) / 2;
  const bX = b ? pad + (width - 2 * pad - b.w) / 2 : 0;
  const bY = pad + a.h + gap;
  const metaY = pad + a.h + (b ? gap + b.h : 0) + metaHeight - 4;
  // Re-wrap each child SVG with explicit width/height so the outer document
  // honours its size when nested in a <g>. bwip-js only sets viewBox.
  const wrap = (s: { w: number; h: number; inner: string }) =>
    s.inner.replace(
      /<svg[^>]*>/,
      `<svg xmlns="http://www.w3.org/2000/svg" width="${s.w}" height="${s.h}" viewBox="0 0 ${s.w} ${s.h}">`,
    );
  const parts: string[] = [];
  parts.push(`<?xml version="1.0" encoding="UTF-8"?>`);
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
  );
  parts.push(`<rect width="${width}" height="${height}" fill="#ffffff" />`);
  parts.push(`<g transform="translate(${aX}, ${pad})">${wrap(a)}</g>`);
  if (b) {
    parts.push(`<g transform="translate(${bX}, ${bY})">${wrap(b)}</g>`);
  }
  if (metaLine) {
    parts.push(
      `<text x="${width / 2}" y="${metaY}" text-anchor="middle" font-family="sans-serif" font-size="12" fill="#111">${escapeXml(
        metaLine,
      )}</text>`,
    );
  }
  parts.push(`</svg>`);
  return parts.join("\n");
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function compositeCanvas(
  primary: HTMLCanvasElement,
  secondary: HTMLCanvasElement | null,
  metaLine: string | null,
): HTMLCanvasElement {
  const pad = 12;
  const gap = secondary ? 14 : 0;
  const metaHeight = metaLine ? 24 : 0;
  const width = Math.max(primary.width, secondary?.width ?? 0) + pad * 2;
  const height = primary.height + (secondary ? gap + secondary.height : 0) + metaHeight + pad * 2;
  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const ctx = out.getContext("2d");
  if (!ctx) return out;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(primary, pad + (width - 2 * pad - primary.width) / 2, pad);
  if (secondary) {
    ctx.drawImage(
      secondary,
      pad + (width - 2 * pad - secondary.width) / 2,
      pad + primary.height + gap,
    );
  }
  if (metaLine) {
    ctx.fillStyle = "#111111";
    ctx.font = "14px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(metaLine, width / 2, height - pad);
  }
  return out;
}

function ItfLabelGeneratorPage() {
  const [gtin, setGtin] = useState("1061414100041");
  const [batch, setBatch] = useState("");
  const [expiry, setExpiry] = useState("");
  const [scale, setScale] = useState(3);
  const [bearerBars, setBearerBars] = useState(true);
  const [showText, setShowText] = useState(true);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [labelWidthPx, setLabelWidthPx] = useState(0);

  const gtinError = useMemo(() => validateGtin(gtin), [gtin]);
  const batchError = useMemo(() => validateBatch(batch), [batch]);
  const expiryError = useMemo(() => validateExpiry(expiry), [expiry]);

  const fullGtin = useMemo(() => (gtinError ? "" : withCheckDigit(gtin)), [gtin, gtinError]);
  const secondaryPayload = useMemo(() => {
    if (batchError || expiryError) return "";
    const parts: string[] = [];
    if (batch) parts.push(`(10)${batch}`);
    if (expiry) parts.push(`(17)${expiry}`);
    return parts.join("");
  }, [batch, expiry, batchError, expiryError]);

  const itfCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const gs1CanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [itfSvg, setItfSvg] = useState<string>("");
  const [gs1Svg, setGs1Svg] = useState<string>("");

  useEffect(() => {
    setRenderError(null);
    if (gtinError) {
      setItfSvg("");
      setGs1Svg("");
      setLabelWidthPx(0);
      const c = itfCanvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      const g = gs1CanvasRef.current;
      if (g) g.getContext("2d")?.clearRect(0, 0, g.width, g.height);
      return;
    }
    let cancelled = false;
    loadBwip().then((bwipjs) => {
      if (cancelled) return;
      try {
        const itf = renderItf14(bwipjs, itfCanvasRef.current, fullGtin, scale, bearerBars, showText);
        setItfSvg(itf?.svg ?? "");
        const primaryW = itfCanvasRef.current?.width ?? 0;
        let totalW = primaryW;
        if (secondaryPayload) {
          const sec = renderGs1128(bwipjs, gs1CanvasRef.current, secondaryPayload, scale, showText);
          setGs1Svg(sec?.svg ?? "");
          totalW = Math.max(totalW, gs1CanvasRef.current?.width ?? 0);
        } else {
          setGs1Svg("");
          const g = gs1CanvasRef.current;
          if (g) g.getContext("2d")?.clearRect(0, 0, g.width, g.height);
        }
        setLabelWidthPx(totalW);
      } catch (e) {
        setRenderError(e instanceof Error ? e.message : "Failed to render label.");
        setItfSvg("");
        setGs1Svg("");
        setLabelWidthPx(0);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fullGtin, secondaryPayload, scale, bearerBars, showText, gtinError]);

  const metaLine = useMemo(() => {
    if (!batch && !expiry) return null;
    const bits: string[] = [];
    if (batch && !batchError) bits.push(`LOT ${batch}`);
    if (expiry && !expiryError) bits.push(`EXP ${formatExpiryHuman(expiry)}`);
    return bits.length > 0 ? bits.join("   ") : null;
  }, [batch, expiry, batchError, expiryError]);

  const labelWidthMm = labelWidthPx > 0 ? (labelWidthPx / 96) * 25.4 : 0;

  const downloadSvg = () => {
    if (!itfSvg) return;
    const composite = buildCompositeSvg({ svg: itfSvg }, gs1Svg ? { svg: gs1Svg } : null, metaLine);
    const blob = new Blob([composite], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(fullGtin)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPng = () => {
    const primary = itfCanvasRef.current;
    if (!primary) return;
    const secondary = gs1Svg ? gs1CanvasRef.current : null;
    const out = compositeCanvas(primary, secondary, metaLine);
    out.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(fullGtin)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const previewBlocked = !!gtinError;

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="ITF-14 Carton Label Generator"
      description="Generate a print-ready ITF-14 carton label with bearer bars and optional GS1-128 batch / expiry — fully client-side, PNG or SVG output. Enter 13 digits and the GS1 check digit is added for you, or enter all 14 and the check digit is validated before the label renders."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the ITF-14 carton label generator"
            about={
              <>
                <p>
                  ITF-14 is the GS1 barcode for outer cases and shipping cartons. It encodes the
                  14-digit GTIN of the unit packed inside the box, framed by heavy bearer bars that
                  equalise printing pressure on corrugated board and prevent partial scans where a
                  scanner only sees part of the symbol.
                </p>
                <p>
                  This generator builds a one-up carton label directly in your browser with the
                  open-source bwip-js library. Enter a 13- or 14-digit GTIN (the check digit is computed
                  automatically), and optionally add a batch / lot number and expiry date — when either
                  is present the label includes a GS1-128 secondary barcode underneath
                  carrying <code>(10)</code> and <code>(17)</code> Application Identifiers, which is how GS1
                  recommends carrying carton attributes that don&apos;t belong inside the ITF-14
                  itself.
                </p>
              </>
            }
            whenToUse={[
              "Labelling outer cases for a shipment to a retailer's distribution centre.",
              "Producing carton barcodes from a GTIN-13 by computing the indicator digit and check digit.",
              "Adding batch / lot and expiry to a carton without changing the ITF-14 GTIN.",
              "Replacing damaged carton labels on the warehouse floor.",
              "Prototyping pack sizes (inner / outer) before sending artwork to the printer.",
              "Producing GS1-compliant proofs for a retailer's onboarding checklist.",
            ]}
            howItWorks={[
              {
                title: "Enter the GTIN-14",
                desc: "Paste the full 14-digit GTIN, or the first 13 digits and let the generator compute the check digit.",
              },
              {
                title: "Add batch / expiry (optional)",
                desc: "Provide AI 10 (lot, up to 20 chars) and AI 17 (YYMMDD). A GS1-128 secondary appears under the ITF-14 with (10) and (17).",
              },
              {
                title: "Confirm bearer bars",
                desc: "Bearer bars are on by default and required by GS1 for direct-print on corrugated. Turn them off only for pre-printed label stock that has its own border.",
              },
              {
                title: "Adjust scale",
                desc: "Pick a scale that gets you close to the GS1 nominal 152.4 mm width on your target printer. Use 'Actual size' in the print dialog.",
              },
              {
                title: "Download SVG or PNG",
                desc: "SVG for crisp print at any size; PNG for proofing in slides, emails or label-design tools.",
              },
            ]}
            whatToAvoid={[
              "Don't stuff batch / expiry inside the ITF-14 — ITF-14 is GTIN-only. Use the GS1-128 secondary that this tool adds.",
              "Don't print below the GS1 50% magnification floor (76 mm width) for ITF-14, even on small cartons — scan rates drop sharply.",
              "Don't omit bearer bars when direct-printing on corrugated; they are part of the symbol, not decoration.",
              "Don't reuse the same batch / expiry across different GTINs on the same pallet — receivers expect each carton's secondary to match its GTIN.",
              "Don't issue a brand-new GTIN you haven't been allocated by GS1; the symbol will scan but won't resolve in retail master data.",
            ]}
            tips={[
              "Use SVG as the canonical export and rasterise to PNG only for the final asset — labels need to scale to several sizes.",
              "Round-trip-test one printed label with the Online Barcode Reader and the Data Matrix Reader before printing a full run.",
              "If your printer supports it, hand the SVG straight to the label driver — many thermal label printers accept SVG natively and avoid the rasterisation step.",
              "For new pack-size GTINs derived from a unit GTIN, change the indicator digit (first digit) and recompute the check digit — the GTIN Validator on this site does this for you.",
              "Pair the carton label with a GS1 Digital Link on the consumer unit so the consumer pack is ready for the 2D-capable checkout scanners of GS1 Sunrise 2027.",
            ]}
          />
          <PrivacyNote />
          <ItfSections />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="gtin">GTIN-14</Label>
              <Input
                id="gtin"
                value={gtin}
                onChange={(e) => setGtin(e.target.value.trim())}
                inputMode="numeric"
                spellCheck={false}
                autoComplete="off"
                className="font-mono"
                placeholder="10614141000415"
              />
              <p className="text-xs text-muted-foreground">
                13 or 14 digits. Check digit is auto-computed if you enter 13.
              </p>
              {gtinError && (
                <p className="flex items-center gap-1 text-xs text-destructive">
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                  {gtinError}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="batch">
                  Batch / Lot <span className="text-muted-foreground">(AI 10, optional)</span>
                </Label>
                <Input
                  id="batch"
                  value={batch}
                  onChange={(e) => setBatch(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  className="font-mono"
                  placeholder="ABC123"
                  maxLength={20}
                />
                {batchError && (
                  <p className="flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                    {batchError}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiry">
                  Expiry <span className="text-muted-foreground">(AI 17, YYMMDD)</span>
                </Label>
                <Input
                  id="expiry"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value.replace(/\D/g, ""))}
                  inputMode="numeric"
                  spellCheck={false}
                  autoComplete="off"
                  className="font-mono"
                  placeholder="261231"
                  maxLength={6}
                />
                {expiryError && (
                  <p className="flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                    {expiryError}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Scale</Label>
                <span className="text-xs text-muted-foreground">{scale}x</span>
              </div>
              <Slider
                min={2}
                max={6}
                step={1}
                value={[scale]}
                onValueChange={(v) => setScale(v[0])}
              />
              {labelWidthMm > 0 && (
                <p className="text-xs text-muted-foreground">
                  Preview is {labelWidthMm.toFixed(1)} mm wide at 96 DPI. GS1 nominal ITF-14 is
                  152.4 mm (range about 76 – 152 mm).
                </p>
              )}
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label htmlFor="bearer" className="cursor-pointer">
                Include bearer bars
              </Label>
              <Switch id="bearer" checked={bearerBars} onCheckedChange={setBearerBars} />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label htmlFor="show-text" className="cursor-pointer">
                Show human-readable text
              </Label>
              <Switch id="show-text" checked={showText} onCheckedChange={setShowText} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border bg-white p-4">
              {renderError ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{renderError}</span>
                </div>
              ) : previewBlocked ? (
                <div className="text-sm text-muted-foreground">
                  Enter a valid 13- or 14-digit GTIN to preview the label.
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <canvas
                    ref={itfCanvasRef}
                    className="max-w-full"
                    aria-label="Generated ITF-14 carton barcode preview"
                  />
                  {secondaryPayload && (
                    <canvas
                      key="gs1-128-canvas"
                      ref={gs1CanvasRef}
                      className="max-w-full"
                      aria-label="Optional GS1-128 batch and expiry barcode preview"
                    />
                  )}
                  {metaLine && (
                    <div className="text-xs font-medium text-muted-foreground">{metaLine}</div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="text-xs font-medium text-muted-foreground">Encoded values</div>
              <code className="mt-1 block break-all text-xs">ITF-14: {fullGtin || "—"}</code>
              {secondaryPayload && (
                <code className="mt-1 block break-all text-xs">GS1-128: {secondaryPayload}</code>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline">ITF-14</Badge>
              {bearerBars && <Badge variant="outline">Bearer bars</Badge>}
              {secondaryPayload && <Badge variant="outline">GS1-128 attribute barcode</Badge>}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={previewBlocked}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={previewBlocked || !itfSvg}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintOpen(true)}
                disabled={previewBlocked || !itfSvg}
              >
                <Printer className="h-4 w-4" aria-hidden />
                Print
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`ITF-14 · ${fullGtin || "carton"}`}
        filename={safeFilename(fullGtin)}
        svgMarkup={
          itfSvg
            ? buildCompositeSvg({ svg: itfSvg }, gs1Svg ? { svg: gs1Svg } : null, metaLine)
            : ""
        }
        defaultCaption=""
        defaults={{
          layoutKind: "single",
          widthMm: 150,
          sheetPreset: "averyL7163",
          paper: "A4",
          orientation: "portrait",
        }}
      />
    </ToolLayout>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        The ITF-14 and GS1-128 are rendered locally with bwip-js. Your GTIN, batch number and
        expiry date never leave your browser.
      </p>
    </>
  );
}

function ItfSections() {
  return (
    <>
      <h2>What Is an ITF-14 Barcode?</h2>
      <p>An ITF-14 barcode carries the 14-digit GTIN of a case or carton, printed in Interleaved 2 of 5 inside a bearer bar. Warehouse scanners read it when cartons are received, stored and shipped.</p>
      <p>To create an ITF-14 barcode from a GTIN-13, type an indicator digit and the first 12 digits of the GTIN-13; the generator adds the new check digit, as the <Link to="/check-digit-calculator">check digit calculator</Link> does. The <Link to="/barcode-guides/what-is-a-gtin">guide to GTINs</Link> explains indicator digits.</p>
      <h3>ITF-14 vs ITF</h3>
      <p>ITF encodes digits in pairs, one in the bars and one in the spaces, so it holds any even number of digits. The ITF-14 barcode format is the GS1 use of it: exactly 14 digits, a GS1 check digit and a bearer bar. See the <Link to="/barcode-guides/barcode-formats">barcode formats guide</Link> for other symbologies.</p>
      <h2>ITF-14 Barcode Size and Bearer Bars</h2>
      <p>The ITF-14 barcode size follows its narrow bar width, which the scale slider changes; the hint under the preview gives the width in millimeters. The bearer bar is the heavy border that spreads printing plate pressure and stops partial scans.</p>
      <h3>Do All ITF Barcodes Need Bearer Bars?</h3>
      <p>Every ITF-14 needs one: a full frame when printed with plates on corrugated, or bars along the top and bottom on labels. The switch here draws the full frame; turn it off only for label stock with a printed frame. Plain ITF outside GS1 follows the rules of its own application.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-generator",
      label: "Online Barcode Generator",
      desc: "Single-barcode generator with UPC, EAN, Code 128, ITF-14 and Codabar.",
    },
    {
      path: "/bulk-barcode-generator",
      label: "Bulk Barcode Generator",
      desc: "Print many ITF-14 carton labels at once from a pasted list of GTINs.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify GTIN-14 check digits before sending artwork to the printer.",
    },
    {
      path: "/gs1-digital-link-generator",
      label: "GS1 Digital Link Generator",
      desc: "Generate a Sunrise-2027 GTIN + batch + expiry as a QR or Data Matrix.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Work out the GTIN-14 check digit before you build the carton label.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode Formats Guide",
      desc: "How ITF-14 compares with Code 128, EAN and the 2D formats.",
    },
    {
      path: "/barcode-guides/what-is-a-gtin",
      label: "What is a GTIN?",
      desc: "Plain-English guide to GTIN-8/12/13/14 and check digits.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/itf-14-label-generator")({
  component: ItfLabelGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode ITF-14 Carton Label Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
